import React, { useState } from 'react';
import { Heart, TrendingUp, FileText, Activity, AlertCircle, Calendar } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const HealthRecords = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('maternal');

  // Sample data - in a real app, this would come from context or API
  const maternalHealthData = {
    pregnancyStatus: 'Second Trimester',
    dueDate: '2024-08-15',
    lastCheckup: '2024-03-20',
    nextCheckup: '2026-03-12',
    weight: '68 kg',
    bloodPressure: '120/80 mmHg',
    notes: 'All vitals normal. Continue prenatal vitamins.'
  };

  const healthRecords = [
    {
      id: 1,
      date: '2024-03-20',
      type: 'Prenatal Checkup',
      doctor: 'Dr. Sarah Johnson',
      findings: 'Normal fetal development, mother healthy',
      attachments: 2
    },
    {
      id: 2,
      date: '2024-02-15',
      type: 'Blood Test',
      doctor: 'Dr. Michael Chen',
      findings: 'All levels normal, iron supplement recommended',
      attachments: 1
    },
    {
      id: 3,
      date: '2024-01-10',
      type: 'Ultrasound',
      doctor: 'Dr. Sarah Johnson',
      findings: 'Healthy fetal growth, no complications',
      attachments: 3
    }
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('healthRecords')}</h1>
              <p className="text-gray-600 text-sm">{t('manageMaternalHealth')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 bg-white rounded-xl p-2 shadow-sm border border-gray-200">
          <button
            onClick={() => setActiveTab('maternal')}
            className={`flex-1 min-w-[140px] px-4 py-3 font-medium rounded-lg transition-all ${
              activeTab === 'maternal'
                ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Heart className="w-4 h-4" />
              <span className="text-sm">{t('maternalHealth')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 min-w-[140px] px-4 py-3 font-medium rounded-lg transition-all ${
              activeTab === 'history'
                ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              <span className="text-sm">{t('medicalHistory')}</span>
            </div>
          </button>
        </div>

        {/* Maternal Health Tab */}
        {activeTab === 'maternal' && (
          <div className="space-y-4">
            {/* Current Status */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Heart className="w-5 h-5 text-emerald-600" />
                </div>
                {t('currentPregnancyStatus')}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-100">
                  <label className="text-xs font-medium text-emerald-700 uppercase tracking-wide">{t('status')}</label>
                  <p className="text-lg font-bold text-gray-900 mt-1">{maternalHealthData.pregnancyStatus}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-100">
                  <label className="text-xs font-medium text-emerald-700 uppercase tracking-wide">{t('expectedDueDate')}</label>
                  <p className="text-lg font-bold text-gray-900 mt-1">{new Date(maternalHealthData.dueDate).toLocaleDateString()}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">{t('lastCheckup')}</label>
                  <p className="text-base font-semibold text-gray-800 mt-1">{new Date(maternalHealthData.lastCheckup).toLocaleDateString()}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                  <label className="text-xs font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {t('nextCheckup')}
                  </label>
                  <p className="text-base font-bold text-amber-700 mt-1">{new Date(maternalHealthData.nextCheckup).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Vitals */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                {t('latestVitals')}
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="relative overflow-hidden p-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl text-white shadow-lg">
                  <p className="text-xs font-medium mb-2 opacity-90">{t('weight')}</p>
                  <p className="text-3xl font-bold">{maternalHealthData.weight}</p>
                  <div className="absolute -bottom-4 -right-4 opacity-10">
                    <TrendingUp className="w-24 h-24" />
                  </div>
                </div>
                <div className="relative overflow-hidden p-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl text-white shadow-lg">
                  <p className="text-xs font-medium mb-2 opacity-90">{t('bloodPressure')}</p>
                  <p className="text-3xl font-bold">{maternalHealthData.bloodPressure}</p>
                  <div className="absolute -bottom-4 -right-4 opacity-10">
                    <Activity className="w-24 h-24" />
                  </div>
                </div>
                <div className="relative overflow-hidden p-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl text-white shadow-lg">
                  <p className="text-xs font-medium mb-2 opacity-90">{t('weeksPregnant')}</p>
                  <p className="text-3xl font-bold">24</p>
                  <div className="absolute -bottom-4 -right-4 opacity-10">
                    <Heart className="w-24 h-24" />
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-l-4 border-emerald-500 p-5 rounded-xl shadow-sm">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-emerald-500 rounded-lg flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-emerald-900 text-sm mb-1">{t('latestNotes')}</h3>
                  <p className="text-emerald-800 text-sm">{maternalHealthData.notes}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Medical History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-3">
            {healthRecords.map(record => (
              <div key={record.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md hover:border-emerald-200 transition-all">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <FileText className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-gray-900">{record.type}</h3>
                        <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {new Date(record.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="ml-11 space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[80px]">Doctor:</span>
                        <span className="text-gray-600">{record.doctor}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[80px]">Findings:</span>
                        <span className="text-gray-600">{record.findings}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700 min-w-[80px]">{t('attachments')}:</span>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                          {record.attachments} file(s)
                        </span>
                      </div>
                    </div>
                  </div>
                  <button className="px-4 py-2 text-emerald-600 hover:bg-emerald-50 rounded-lg font-semibold text-sm transition-colors border border-emerald-200 hover:border-emerald-300">
                    {t('viewDetails')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthRecords;